import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const BusinessContent = ({ analysisData }) => {
    const [businessContent, setBusinessContent] = useState({
        tagline: analysisData?.business_tagline || '',
        description: analysisData?.business_description || '',
        summary: analysisData?.business_summary || {}
    });

    useEffect(() => {
        setBusinessContent({
            tagline: analysisData?.business_tagline || '',
            description: analysisData?.business_description || '',
            summary: analysisData?.business_summary || {}
        });
    }, [analysisData]);

    const hasBusinessContent = businessContent.tagline || businessContent.description || (businessContent.summary && Object.keys(businessContent.summary).length > 0);

    return (
        <div className="tab-content">
            <div className="business-content-header">
                <h3>Business Information</h3>
                <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '1.5rem' }}>
                    {hasBusinessContent
                        ? 'AI-generated business content based on your website analysis during scraping. Business content is automatically generated using your website\'s actual title, description, and content data.'
                        : 'Business content is automatically generated during website analysis using AI. If no content appears, the website may not have sufficient metadata for analysis.'
                    }
                </p>
            </div>

            {!hasBusinessContent && (
                <div className="business-content-empty">
                    <div className="empty-state">
                        <div className="empty-icon">📄</div>
                        <h4>No Business Content Available</h4>
                        <p>Business content is automatically generated during website analysis. This website may not have sufficient metadata (title, description, content) for AI analysis, or there may have been an issue with the generation process.</p>
                    </div>
                </div>
            )}

            {hasBusinessContent && (
                <div className="business-content-display">
                    {businessContent.tagline && (
                        <div className="business-field">
                            <h4>Business Tagline</h4>
                            <div className="content-display">
                                <p className="tagline-text">{businessContent.tagline}</p>
                            </div>
                        </div>
                    )}

                    {businessContent.description && (
                        <div className="business-field">
                            <h4>Business Description</h4>
                            <div className="content-display description-container">
                                <div className="description-text">{businessContent.description}</div>
                                <div className="description-meta">
                                    <small>
                                        {businessContent.description.length} characters •
                                        {Math.ceil(businessContent.description.split(' ').length / 200)} min read
                                    </small>
                                </div>
                            </div>
                        </div>
                    )}

                    {businessContent.summary?.industry && (
                        <div className="business-summary">
                            <h4>Business Summary</h4>
                            <div className="summary-grid">
                                <div className="summary-item">
                                    <strong>Industry:</strong> {businessContent.summary.industry}
                                </div>
                                <div className="summary-item">
                                    <strong>Business Type:</strong> {businessContent.summary.businessType || 'Unknown'}
                                </div>
                                <div className="summary-item">
                                    <strong>Target Audience:</strong> {businessContent.summary.targetAudience || 'General Audience'}
                                </div>
                                {businessContent.summary.keyFeatures?.length > 0 && (
                                    <div className="summary-item">
                                        <strong>Key Features:</strong>
                                        <ul>
                                            {businessContent.summary.keyFeatures.slice(0, 5).map((feature) => (
                                                <li key={feature}>{feature}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <style>{`
                .business-content-header h3 {
                    color: #1e293b;
                    margin-bottom: 0.5rem;
                }

                .business-content-empty {
                    display: flex;
                    justify-content: center;
                    padding: 3rem 1rem;
                }

                .empty-state {
                    text-align: center;
                    max-width: 500px;
                }

                .empty-icon {
                    font-size: 3rem;
                    margin-bottom: 1rem;
                }

                .empty-state h4 {
                    color: #1e293b;
                    margin-bottom: 0.5rem;
                }

                .empty-state p {
                    color: #64748b;
                    margin-bottom: 1.5rem;
                    line-height: 1.6;
                }

                .business-field {
                    margin-bottom: 2rem;
                }

                .business-field h4 {
                    color: #1e293b;
                    margin-bottom: 0.5rem;
                    font-size: 1.1rem;
                }

                .content-display {
                    background: #f8fafc;
                    padding: 1rem;
                    border-radius: 8px;
                    border-left: 4px solid #3b82f6;
                }

                .description-container {
                    position: relative;
                }

                .description-meta {
                    margin-top: 1rem;
                    padding-top: 0.75rem;
                    border-top: 1px solid #e2e8f0;
                }

                .description-meta small {
                    color: #94a3b8;
                    font-size: 0.75rem;
                }

                .tagline-text {
                    font-size: 1.2rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0;
                }

                .description-text {
                    color: #475569;
                    line-height: 1.7;
                    margin: 0;
                    white-space: pre-line;
                    max-height: 400px;
                    overflow-y: auto;
                }

                .business-summary {
                    margin-top: 2rem;
                    padding: 1rem;
                    background: #f1f5f9;
                    border-radius: 8px;
                }

                .business-summary h4 {
                    color: #1e293b;
                    margin-bottom: 1rem;
                }

                .summary-grid {
                    display: grid;
                    gap: 0.75rem;
                }

                .summary-item {
                    color: #475569;
                    font-size: 0.9rem;
                }

                .summary-item ul {
                    margin: 0.25rem 0 0 1rem;
                    padding: 0;
                }

                .business-info {
                    margin-top: 2rem;
                    padding: 1rem;
                    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
                    border-radius: 8px;
                    border: 1px solid #bae6fd;
                }

                .info-box h5 {
                    color: #0369a1;
                    margin-bottom: 0.5rem;
                    font-size: 1rem;
                }

                .info-box p, .info-box ul {
                    color: #0c4a6e;
                    font-size: 0.85rem;
                    line-height: 1.5;
                }

                .info-box ul {
                    margin: 0.5rem 0 0.5rem 1rem;
                    padding: 0;
                }

                .btn {
                    padding: 0.5rem 1rem;
                    border-radius: 6px;
                    border: none;
                    font-size: 0.9rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-decoration: none;
                    display: inline-block;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-primary {
                    background: #3b82f6;
                    color: white;
                }

                .btn-primary:hover:not(:disabled) {
                    background: #2563eb;
                }

                @media (max-width: 640px) {
                    .btn {
                        width: 100%;
                        text-align: center;
                    }
                }
            `}</style>
        </div>
    );
};

BusinessContent.propTypes = {
    analysisData: PropTypes.object.isRequired
};

export default BusinessContent;